import { useCallback, useEffect, useRef, useState } from "react";

import { useCabinetRefresh } from "./CabinetRefresh";
import CabinetStationsApi from "./services/CabinetStationsApi";
import { STATIONS_REFRESH_MS } from "./stationsView";
import { MyPlace, MyStation } from "./types";

/**
 * `Ф1` (кабинет ПЗС): загрузка раздела «Станции».
 *
 * Первая загрузка — со скелетом; дальше — тихое обновление раз в 15 с. Сбой
 * тихого обновления не стирает экран: данные остаются, индикатор в шапке
 * переходит в «не удалось», а `version` из контекста даёт кнопке «Обновить»
 * повторить запрос.
 */
export function useCabinetStations() {
  const { version, markUpdated, markFailed } = useCabinetRefresh();
  const [stations, setStations] = useState<MyStation[]>([]);
  const [places, setPlaces] = useState<MyPlace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const firstLoad = useRef(true);

  const load = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      try {
        const [nextStations, nextPlaces] = await Promise.all([
          CabinetStationsApi.getMyStations(),
          CabinetStationsApi.getMyPlaces(),
        ]);

        setStations(nextStations);
        setPlaces(nextPlaces);
        setError(null);
        markUpdated();
      } catch {
        // Данные не стираем: показываем последнее и говорим, что обновить не
        // удалось. Полноэкранная ошибка — только когда показывать нечего.
        markFailed();
        setError((current) =>
          current ?? "Не удалось загрузить станции. Попробуйте ещё раз.",
        );
      } finally {
        setLoading(false);
      }
    },
    [markUpdated, markFailed],
  );

  // Первый заход и ручной повтор из шапки (`version`).
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      load({ silent: false });
      return;
    }

    load({ silent: true });
  }, [version, load]);

  // Автообновление раз в 15 с.
  useEffect(() => {
    const timer = setInterval(() => {
      load({ silent: true });
    }, STATIONS_REFRESH_MS);

    return () => clearInterval(timer);
  }, [load]);

  return {
    loading,
    error,
    stations,
    places,
    reload: () => load({ silent: true }),
  };
}
